/**
 * Conduct Check-In Workflow Tour Steps
 * Guides People Leaders through the check-in process within ConductReviewWorkspace
 * Triggered on first open of the check-in workspace or via a help/tour button
 */

import { DemoStep, scrollAndHighlight } from './tourHelpers'

export const conductReviewTourSteps: DemoStep[] = [
  // Step 1: Welcome
  {
    element: 'body',
    popover: {
      title: '📋 Conducting a 1:1 Check-In',
      description: 'This workspace guides you through the complete check-in process — from discussing each KPA with impact portfolio entries, to gathering feedback insights, to finalising an overall indicator. Let\'s walk through it.',
      side: 'bottom',
      align: 'center'
    }
  },

  // Step 2: Check-In Header / Context
  {
    element: '[data-tour="review-header"]',
    popover: {
      title: '📊 Check-In Context',
      description: 'The header shows the team member\'s name, check-in type (Year-End, Mid-Year, Q1, Q3, or JIT), and scheduled date. This context stays visible throughout the check-in so you always know where you are.',
      side: 'bottom'
    }
  },

  // Step 3: KPA Navigator (left panel)
  {
    element: '[data-tour="kpa-navigator"]',
    popover: {
      title: '📑 KPA Navigator',
      description: 'The left panel lists all Key Performance Areas for this check-in period. Work through each KPA one by one — your progress is tracked so you can see which KPAs you\'ve discussed and rated. Click any KPA to jump directly to it.',
      side: 'right'
    },
    onHighlighted: () => {
      scrollAndHighlight('[data-tour="kpa-navigator"]')
    }
  },

  // Step 4: Objectives & KPIs Sub-tab
  {
    element: '[data-tour="kpa-objectives-tab"]',
    popover: {
      title: '🎯 Objectives & KPIs',
      description: 'For each KPA, review the linked individual objectives and their underlying KPIs. See current progress percentages, target values, and actual values. This is the data foundation for your indicator discussion.',
      side: 'bottom'
    }
  },

  // Step 5: Evidence Sub-tab
  {
    element: '[data-tour="kpa-evidence-tab"]',
    popover: {
      title: '📁 Impact Portfolio',
      description: 'View all impact portfolio entries submitted by the team member for this KPA — filtered by classification (Success, Challenge, Pending, Insight). These entries provide the objective basis for your indicator decisions.',
      side: 'bottom'
    }
  },

  // Step 6: Discussion Points
  {
    element: '[data-tour="kpa-discussion"]',
    popover: {
      title: '💬 Check-In Discussion',
      description: 'Structure your conversation around three areas: What went well (strengths), Challenges faced (areas for improvement), and Actions for next period (growth plans). Use the <strong>AI Discussion Guide</strong> for impact portfolio-based conversation prompts.',
      side: 'top'
    },
    onHighlighted: () => {
      scrollAndHighlight('[data-tour="kpa-discussion"]')
    }
  },

  // Step 7: KPA Rating
  {
    element: '[data-tour="kpa-rating"]',
    popover: {
      title: '⭐ KPA Impact & Growth Indicator',
      description: 'Rate this KPA on the 4-tier scale: Exceptional, Full, Not-Yet-Full, or Weak. Add comments to justify your indicator. The <strong>AI Impact & Growth Indicator Assistant</strong> can suggest an indicator based on the available impact portfolio.',
      side: 'top'
    },
    onHighlighted: () => {
      scrollAndHighlight('[data-tour="kpa-rating"]')
    }
  },

  // Step 8: Stakeholder Feedback Section
  {
    element: '[data-tour="review-feedback-tab"]',
    popover: {
      title: '👥 Stakeholder Feedback',
      description: 'View aggregated 360/stakeholder feedback for this team member. The AI Check-In Assistant synthesises feedback themes and highlights key strengths and growth areas to inform your overall assessment.',
      side: 'bottom'
    }
  },

  // Step 9: Finalise Check-In
  {
    element: '[data-tour="review-finalize-tab"]',
    popover: {
      title: '✅ Finalise Check-In',
      description: 'Assign an overall impact & growth indicator, write your justification, and review the AI-generated synthesis report. You can optionally transfer identified strengths and growth areas directly into the team member\'s Growth Plan.',
      side: 'bottom'
    }
  },

  // Step 10: Complete & Lock
  {
    element: '[data-tour="review-complete-btn"]',
    popover: {
      title: '🏁 Complete & Lock',
      description: 'Click \'Complete Check-In\' to finalise. Once completed, the check-in becomes read-only — all indicators and impact portfolio entries are locked. Make sure you\'re satisfied with your indicators and comments before completing.',
      side: 'top'
    },
    onHighlighted: () => {
      scrollAndHighlight('[data-tour="review-complete-btn"]')
    }
  }
]
